import { inject, injectable } from 'tsyringe';
import { OWInternalExtensions } from '../../odk-internal-ts/ow-internal-extensions';
import { OWExtension } from '../../odk-ts';
import { IOWExtensionWrapper } from 'app-core/src/interfaces/ow-wrappers/ow-extension-wrapper.interface';
import {
  kNameOfRegistryWrapper,
  kOwElectronRegistryPath,
} from 'app-core/src/constants/constants';
import { OwRegistryWrapper } from '../services/registry-wrapper';

// -----------------------------------------------------------------------------
// Testable wrapper for OWExtension
@injectable()
export class OWExtensionWrapper implements IOWExtensionWrapper {
  private owExtension: OWExtension = null;

  // ---------------------------------------------------------------------------
  public constructor(
    @inject(kNameOfRegistryWrapper)
    private readonly registryWrapper: OwRegistryWrapper,
  ) {}

  // ---------------------------------------------------------------------------
  public get manifest(): overwolf.extensions.Manifest {
    return this.owExtension?.manifest;
  }

  // ---------------------------------------------------------------------------
  public async initialize() {
    this.owExtension = await OWExtension.fromCurrent();
  }

  // ---------------------------------------------------------------------------
  public async initializeForExtension(extensionId: string) {
    this.owExtension = await OWExtension.fromExtensionId(extensionId);
  }

  // ---------------------------------------------------------------------------
  public async changeExtensionEnableState(uid: string, enable: boolean) {
    await OWInternalExtensions.changeExtensionEnableState(uid, enable);
  }

  // ---------------------------------------------------------------------------
  public async addTemporarilyWhitelistedExtension(uid: string) {
    OWInternalExtensions.addTemporarilyWhitelistedExtension(uid);
  }

  // ---------------------------------------------------------------------------
  public async installAnyApp(uid: string) {
    await OWInternalExtensions.installAnyApp(uid);
  }

  // ---------------------------------------------------------------------------
  public async getDownloadedExtensions() {
    return OWInternalExtensions.getDownloadedExtensions();
  }

  // ---------------------------------------------------------------------------
  public async getInstalledAndEnabledExtensions() {
    const downloadedExtensions = await this.getDownloadedExtensions();

    return downloadedExtensions.filter((extension) => {
      return extension.is_installed && extension.is_enabled;
    });
  }

  // ---------------------------------------------------------------------------
  public async getInstalledAndEnabledExtensionUids() {
    const owDownloadedOwExtensions = await this.getDownloadedExtensions();
    const downloadedOwUids = owDownloadedOwExtensions
      .filter((extension) => {
        return extension.is_installed && extension.is_enabled;
      })
      .map((ext) => ext.UID);

    const oweDownloadedExtensions =
      await this.getElectronDownloadedExtensions();

    if (!oweDownloadedExtensions || oweDownloadedExtensions.length === 0) {
      return downloadedOwUids;
    }

    return [...downloadedOwUids, ...oweDownloadedExtensions];
  }

  // ---------------------------------------------------------------------------
  private async getElectronDownloadedExtensions() {
    return this.registryWrapper.getCurrentUserKeyList(kOwElectronRegistryPath);
  }
}
